/**
 * 统一存储适配器
 * 提供统一的存储接口，支持多种存储后端
 */

/* eslint-disable max-classes-per-file */
import { BaseManager } from './BaseManager';
import logger from '@/utils/logger';

// 存储类型枚举
export enum StorageType {
  LOCAL = 'localStorage',
  SESSION = 'sessionStorage',
  MEMORY = 'memory',
  INDEXED_DB = 'indexedDB',
}

// 存储配置接口
export interface StorageConfig {
  type: StorageType;
  namespace?: string;
  encryption?: boolean;
  compression?: boolean;
  ttl?: number; // 生存时间（毫秒）
  maxSize?: number; // 最大存储大小（字节）
}

// 存储项接口
interface StorageItem<T = any> {
  value: T;
  timestamp: number;
  ttl?: number;
  size?: number;
}

// 存储统计接口
export interface StorageStats {
  type: StorageType;
  namespace: string;
  itemCount: number;
  totalSize: number;
  maxSize: number;
  hitCount: number;
  missCount: number;
  errorCount: number;
}

// 基础存储接口
export interface IStorage<T = any> {
  get(key: string): Promise<T | null>;
  set(key: string, value: T, ttl?: number): Promise<void>;
  has(key: string): Promise<boolean>;
  delete(key: string): Promise<boolean>;
  clear(): Promise<void>;
  keys(): Promise<string[]>;
  size(): Promise<number>;
  getStats(): StorageStats;
}

/**
 * 内存存储实现
 */
class MemoryStorage<T> implements IStorage<T> {
  private storage = new Map<string, StorageItem<T>>();

  private config: Required<StorageConfig>;

  private stats: StorageStats;

  constructor(config: StorageConfig) {
    this.config = {
      namespace: 'default',
      encryption: false,
      compression: false,
      ttl: 0,
      maxSize: 10 * 1024 * 1024, // 10MB
      ...config,
    };

    this.stats = {
      type: StorageType.MEMORY,
      namespace: this.config.namespace,
      itemCount: 0,
      totalSize: 0,
      maxSize: this.config.maxSize,
      hitCount: 0,
      missCount: 0,
      errorCount: 0,
    };
  }

  async get(key: string): Promise<T | null> {
    try {
      const item = this.storage.get(key);
      if (!item) {
        this.stats.missCount++;
        return null;
      }

      // 检查TTL
      if (item.ttl && Date.now() - item.timestamp > item.ttl) {
        await this.delete(key);
        this.stats.missCount++;
        return null;
      }

      this.stats.hitCount++;
      return item.value;
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[MemoryStorage] 读取失败:', error);
      return null;
    }
  }

  async set(key: string, value: T, ttl?: number): Promise<void> {
    try {
      const serialized = JSON.stringify(value);
      const size = new Blob([serialized]).size;

      // 检查大小限制
      if (this.stats.totalSize + size > this.stats.maxSize) {
        throw new Error('存储空间不足');
      }

      const item: StorageItem<T> = {
        value,
        timestamp: Date.now(),
        ttl: ttl || this.config.ttl || undefined,
        size,
      };

      // 如果key已存在，先减去旧的大小
      const existingItem = this.storage.get(key);
      if (existingItem) {
        this.stats.totalSize -= existingItem.size || 0;
      } else {
        this.stats.itemCount++;
      }

      this.storage.set(key, item);
      this.stats.totalSize += size;
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[MemoryStorage] 写入失败:', error);
      throw error;
    }
  }

  async has(key: string): Promise<boolean> {
    const value = await this.get(key);
    return value !== null;
  }

  async delete(key: string): Promise<boolean> {
    try {
      const item = this.storage.get(key);
      if (item) {
        this.storage.delete(key);
        this.stats.itemCount--;
        this.stats.totalSize -= item.size || 0;
        return true;
      }
      return false;
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[MemoryStorage] 删除失败:', error);
      return false;
    }
  }

  async clear(): Promise<void> {
    try {
      this.storage.clear();
      this.stats.itemCount = 0;
      this.stats.totalSize = 0;
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[MemoryStorage] 清空失败:', error);
      throw error;
    }
  }

  async keys(): Promise<string[]> {
    return Array.from(this.storage.keys());
  }

  async size(): Promise<number> {
    return this.storage.size;
  }

  getStats(): StorageStats {
    return { ...this.stats };
  }
}

/**
 * LocalStorage存储实现
 */
class LocalStorageAdapter<T> implements IStorage<T> {
  private config: Required<StorageConfig>;

  private stats: StorageStats;

  constructor(config: StorageConfig) {
    this.config = {
      namespace: 'default',
      encryption: false,
      compression: false,
      ttl: 0,
      maxSize: 5 * 1024 * 1024, // 5MB
      ...config,
    };

    this.stats = {
      type: StorageType.LOCAL,
      namespace: this.config.namespace,
      itemCount: 0,
      totalSize: 0,
      maxSize: this.config.maxSize,
      hitCount: 0,
      missCount: 0,
      errorCount: 0,
    };

    this.updateStats();
  }

  private getStorageKey(key: string): string {
    return `${this.config.namespace}:${key}`;
  }

  async get(key: string): Promise<T | null> {
    try {
      const storageKey = this.getStorageKey(key);
      const itemStr = localStorage.getItem(storageKey);

      if (!itemStr) {
        this.stats.missCount++;
        return null;
      }

      const item: StorageItem<T> = JSON.parse(itemStr);

      // 检查TTL
      if (item.ttl && Date.now() - item.timestamp > item.ttl) {
        await this.delete(key);
        this.stats.missCount++;
        return null;
      }

      this.stats.hitCount++;
      return item.value;
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[LocalStorageAdapter] 读取失败:', error);
      return null;
    }
  }

  async set(key: string, value: T, ttl?: number): Promise<void> {
    try {
      const item: StorageItem<T> = {
        value,
        timestamp: Date.now(),
        ttl: ttl || this.config.ttl || undefined,
      };

      const serialized = JSON.stringify(item);
      const size = new Blob([serialized]).size;

      // 检查大小限制
      if (size > this.config.maxSize) {
        throw new Error('数据过大');
      }

      const storageKey = this.getStorageKey(key);
      localStorage.setItem(storageKey, serialized);

      this.updateStats();
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[LocalStorageAdapter] 写入失败:', error);
      throw error;
    }
  }

  async has(key: string): Promise<boolean> {
    const value = await this.get(key);
    return value !== null;
  }

  async delete(key: string): Promise<boolean> {
    try {
      const storageKey = this.getStorageKey(key);
      const existed = localStorage.getItem(storageKey) !== null;
      localStorage.removeItem(storageKey);

      if (existed) {
        this.updateStats();
      }

      return existed;
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[LocalStorageAdapter] 删除失败:', error);
      return false;
    }
  }

  async clear(): Promise<void> {
    try {
      const keysToRemove: string[] = [];
      const prefix = `${this.config.namespace}:`;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => localStorage.removeItem(key));
      this.updateStats();
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[LocalStorageAdapter] 清空失败:', error);
      throw error;
    }
  }

  async keys(): Promise<string[]> {
    try {
      const keys: string[] = [];
      const prefix = `${this.config.namespace}:`;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keys.push(key.substring(prefix.length));
        }
      }

      return keys;
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[LocalStorageAdapter] 获取键列表失败:', error);
      return [];
    }
  }

  async size(): Promise<number> {
    const keys = await this.keys();
    return keys.length;
  }

  getStats(): StorageStats {
    return { ...this.stats };
  }

  private updateStats(): void {
    try {
      let itemCount = 0;
      let totalSize = 0;
      const prefix = `${this.config.namespace}:`;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          itemCount++;
          const value = localStorage.getItem(key);
          if (value) {
            totalSize += new Blob([value]).size;
          }
        }
      }

      this.stats.itemCount = itemCount;
      this.stats.totalSize = totalSize;
    } catch (error) {
      logger.error('[LocalStorageAdapter] 统计更新失败:', error);
    }
  }
}

/**
 * SessionStorage存储实现
 */
class SessionStorageAdapter<T> implements IStorage<T> {
  private config: Required<StorageConfig>;

  private stats: StorageStats;

  constructor(config: StorageConfig) {
    this.config = {
      namespace: 'default',
      encryption: false,
      compression: false,
      ttl: 0,
      maxSize: 5 * 1024 * 1024, // 5MB
      ...config,
    };

    this.stats = {
      type: StorageType.SESSION,
      namespace: this.config.namespace,
      itemCount: 0,
      totalSize: 0,
      maxSize: this.config.maxSize,
      hitCount: 0,
      missCount: 0,
      errorCount: 0,
    };

    this.updateStats();
  }

  private getStorageKey(key: string): string {
    return `${this.config.namespace}:${key}`;
  }

  async get(key: string): Promise<T | null> {
    try {
      const storageKey = this.getStorageKey(key);
      const itemStr = sessionStorage.getItem(storageKey);

      if (!itemStr) {
        this.stats.missCount++;
        return null;
      }

      const item: StorageItem<T> = JSON.parse(itemStr);

      // 检查TTL
      if (item.ttl && Date.now() - item.timestamp > item.ttl) {
        await this.delete(key);
        this.stats.missCount++;
        return null;
      }

      this.stats.hitCount++;
      return item.value;
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[SessionStorageAdapter] 读取失败:', error);
      return null;
    }
  }

  async set(key: string, value: T, ttl?: number): Promise<void> {
    try {
      const item: StorageItem<T> = {
        value,
        timestamp: Date.now(),
        ttl: ttl || this.config.ttl || undefined,
      };

      const serialized = JSON.stringify(item);
      const storageKey = this.getStorageKey(key);
      sessionStorage.setItem(storageKey, serialized);

      this.updateStats();
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[SessionStorageAdapter] 写入失败:', error);
      throw error;
    }
  }

  async has(key: string): Promise<boolean> {
    const value = await this.get(key);
    return value !== null;
  }

  async delete(key: string): Promise<boolean> {
    try {
      const storageKey = this.getStorageKey(key);
      const existed = sessionStorage.getItem(storageKey) !== null;
      sessionStorage.removeItem(storageKey);

      if (existed) {
        this.updateStats();
      }

      return existed;
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[SessionStorageAdapter] 删除失败:', error);
      return false;
    }
  }

  async clear(): Promise<void> {
    try {
      const keysToRemove: string[] = [];
      const prefix = `${this.config.namespace}:`;

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
      this.updateStats();
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[SessionStorageAdapter] 清空失败:', error);
      throw error;
    }
  }

  async keys(): Promise<string[]> {
    try {
      const keys: string[] = [];
      const prefix = `${this.config.namespace}:`;

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(prefix)) {
          keys.push(key.substring(prefix.length));
        }
      }

      return keys;
    } catch (error) {
      this.stats.errorCount++;
      logger.error('[SessionStorageAdapter] 获取键列表失败:', error);
      return [];
    }
  }

  async size(): Promise<number> {
    const keys = await this.keys();
    return keys.length;
  }

  getStats(): StorageStats {
    return { ...this.stats };
  }

  private updateStats(): void {
    try {
      let itemCount = 0;
      let totalSize = 0;
      const prefix = `${this.config.namespace}:`;

      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(prefix)) {
          itemCount++;
          const value = sessionStorage.getItem(key);
          if (value) {
            totalSize += new Blob([value]).size;
          }
        }
      }

      this.stats.itemCount = itemCount;
      this.stats.totalSize = totalSize;
    } catch (error) {
      logger.error('[SessionStorageAdapter] 统计更新失败:', error);
    }
  }
}

/**
 * 存储适配器管理器
 */
export class StorageAdapter extends BaseManager {
  private storages = new Map<string, IStorage>();

  private defaultConfig: StorageConfig = {
    type: StorageType.LOCAL,
    namespace: 'default',
    encryption: false,
    compression: false,
    ttl: 0,
    maxSize: 5 * 1024 * 1024, // 5MB
  };

  constructor() {
    super({
      name: 'storage-adapter',
    });
  }

  protected async onInit(): Promise<void> {
    logger.info('[StorageAdapter] 存储适配器初始化完成');
  }

  protected async onDestroy(): Promise<void> {
    this.storages.clear();
    logger.info('[StorageAdapter] 存储适配器已销毁');
  }

  /**
   * 创建或获取存储实例
   */
  getStorage<T = any>(namespace: string, config?: Partial<StorageConfig>): IStorage<T> {
    const key = `${namespace}-${config?.type || this.defaultConfig.type}`;

    if (this.storages.has(key)) {
      return this.storages.get(key)! as IStorage<T>;
    }

    const finalConfig: StorageConfig = {
      ...this.defaultConfig,
      ...config,
      namespace,
    };

    let storage: IStorage<T>;

    switch (finalConfig.type) {
      case StorageType.MEMORY:
        storage = new MemoryStorage<T>(finalConfig);
        break;
      case StorageType.LOCAL:
        storage = new LocalStorageAdapter<T>(finalConfig);
        break;
      case StorageType.SESSION:
        storage = new SessionStorageAdapter<T>(finalConfig);
        break;
      default:
        storage = new LocalStorageAdapter<T>(finalConfig);
    }

    this.storages.set(key, storage);
    logger.debug(`[StorageAdapter] 创建存储实例: ${namespace} (${finalConfig.type})`);

    return storage;
  }

  /**
   * 删除存储实例
   */
  async removeStorage(namespace: string, type?: StorageType): Promise<boolean> {
    const key = `${namespace}-${type || this.defaultConfig.type}`;
    const storage = this.storages.get(key);

    if (storage) {
      await storage.clear();
      this.storages.delete(key);
      logger.debug(`[StorageAdapter] 删除存储实例: ${key}`);
      return true;
    }

    return false;
  }

  /**
   * 清空所有存储
   */
  async clearAll(): Promise<void> {
    const clearPromises = Array.from(this.storages.values()).map((storage) =>
      storage.clear().catch((error) => logger.error('[StorageAdapter] 清空存储失败:', error))
    );

    await Promise.all(clearPromises);
    logger.debug('[StorageAdapter] 所有存储已清空');
  }

  /**
   * 获取所有存储统计
   */
  getAllStats(): Record<string, StorageStats> {
    const stats: Record<string, StorageStats> = {};
    this.storages.forEach((storage, key) => {
      stats[key] = storage.getStats();
    });
    return stats;
  }

  /**
   * 获取存储实例列表
   */
  getStorageNames(): string[] {
    return Array.from(this.storages.keys());
  }
}

// 导出单例实例
export const storageAdapter = new StorageAdapter();

// 导出便捷函数
export const createStorage = <T = any>(namespace: string, config?: Partial<StorageConfig>) =>
  storageAdapter.getStorage<T>(namespace, config);

export const getStorage = <T = any>(namespace: string, type?: StorageType) =>
  storageAdapter.getStorage<T>(namespace, { type });

export const getLocalStorage = <T = any>(namespace: string) =>
  storageAdapter.getStorage<T>(namespace, { type: StorageType.LOCAL });

export const getSessionStorage = <T = any>(namespace: string) =>
  storageAdapter.getStorage<T>(namespace, { type: StorageType.SESSION });

export const getMemoryStorage = <T = any>(namespace: string) =>
  storageAdapter.getStorage<T>(namespace, { type: StorageType.MEMORY });
